// Effects tab — contextual per selection type (photo / shape / icon). Home of
// the controls that used to live in seven toolbar popovers (shadow, filters,
// gradient). Text no longer routes here: InspectorPanel renders the dedicated
// TextStyleTab (inspector/text/) for text selections.
//
// Universal effects (U2, 2026-07-21): shapes/icons gain an Effects (tiles) +
// Depth (Bevel/3D) surface at the top — the ribbon Row-2 sections unfolded,
// matrix-gated per kind, applied via the object-generic verbs (the drawer-is-
// the-ribbon-unfolded law). See ObjectEffectsSection below.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectionProps, isTextType } from '../hooks/useSelectionProps';
import { TbSlider, TbColorField, TbNumberField } from '../primitives/toolbarPrimitives';
// Universal effects (U2): the shape/icon drawer unfolds the SAME Row-2 effects
// (the drawer-is-the-ribbon-unfolded law) — effect tiles + a Bevel/3D depth
// block, gated PER KIND against the capability matrix and applied through the
// object-generic verbs. Tile art rides each def's tilePreview() (ONE source
// with the ribbon faces + the text drawer tiles — no drift).
import { EFFECT_DEFS, EFFECT_ORDER, effectDefaults } from './text/effectDefs';
import DepthTileFace from './text/depthTileFaces';
import { effectCapability } from '../lib/fx/capabilityMatrix';
import { FX_BEVEL_DEFAULTS } from '../lib/fx/fxBevel.js';
import { FX_EXTRUDE_DEFAULTS } from '../lib/fx/fxExtrude.js';

// Hollow/Trace/Sticker fight a 3D bake (die-cut / see-through looks the extrude
// replaces) — gated while 3D is live, exactly the text drawer's GATED_3D_TILES.
const GATED_3D_TILES = new Set(['hollow', 'trace', 'sticker']);

/** The shape/icon Effects + Depth surface — the ribbon's Row-2 EFFECTS/DEPTH
 *  sections, unfolded. Tiles toggle slot effects through the object-generic
 *  verbs (applyEffect/removeEffect/clearEffects); Bevel rides patchTextFx (an
 *  fx step on any alpha); 3D is matrix-gated until U3 (DYNAMIC read). */
function ObjectEffectsSection({ objProps, apply, commitNow, editor, kind }) {
  const bag = objProps.effects || {};
  const activeIds = new Set([bag.shadow?.id, bag.stroke?.id].filter(Boolean));
  const hasAny = activeIds.size > 0;
  const is3d = !!objProps.is3dActive;

  const onTile = (id) => {
    if (activeIds.has(id)) apply(() => editor?.removeEffect(id), 'now');
    else apply(() => editor?.applyEffect(id, effectDefaults(EFFECT_DEFS[id])), 'now');
  };

  const bevelOn = (objProps.fxBevelDepth ?? 0) > 0;
  const extrudeOn = (objProps.fxExtrudeDepth ?? 0) > 0;
  const extrudeCap = effectCapability(kind, 'extrude');
  const threeDDenied = extrudeCap.mode === 'unavailable';

  return (
    <>
      <Section title="Effects">
        <div className="cce-fx-grid">
          <button
            type="button"
            className={`cce-fx-tile${hasAny ? '' : ' active'}`}
            onClick={() => apply(() => editor?.clearEffects(), 'now')}
          >
            <span className="cce-fx-tile-preview">Aa</span>
            <span className="cce-fx-tile-label">None</span>
          </button>
          {EFFECT_ORDER.map((id) => {
            // Outline face is HIDDEN for shapes (the Border tool owns shape
            // edges — no twins), present + fx-gated for icons.
            if (id === 'outline' && kind === 'shape') return null;
            const def = EFFECT_DEFS[id];
            const cap = effectCapability(kind, id);
            const active = activeIds.has(id);
            const capOff = cap.mode === 'unavailable';
            const threeGate = GATED_3D_TILES.has(id) && is3d;
            // A LIT gated tile stays tappable (its tap REMOVES the effect —
            // trapping a live effect behind a disabled tile is worse).
            const gated = (capOff || threeGate) && !active;
            // "Trace letters" is text wording — shapes/icons have edges
            const label = id === 'trace' && kind !== 'text' ? 'Trace' : def.label;
            const title = capOff ? cap.reason : (threeGate ? 'Turn off 3D to use this effect' : label);
            return (
              <button
                key={id}
                type="button"
                className={`cce-fx-tile${active ? ' active' : ''}${gated ? ' cce-fx-tile-gated' : ''}`}
                disabled={gated}
                title={title}
                onClick={() => onTile(id)}
              >
                <span className="cce-fx-tile-preview" style={def.tilePreview()}>Aa</span>
                <span className="cce-fx-tile-label">{label}</span>
              </button>
            );
          })}
        </div>
      </Section>
      <Section title="Depth">
        <div className="cce-fx-depth">
          <label className="cce-depth-toggle cce-depth-toggle-bevel">
            <input
              type="checkbox"
              checked={bevelOn}
              onChange={(e) => apply(
                () => editor?.patchTextFx('bevel', { depth: e.target.checked ? FX_BEVEL_DEFAULTS.depth : 0 }),
                'now',
              )}
            />
            <DepthTileFace kind="bevel" />
            <span>Bevel</span>
          </label>
          {bevelOn && (
            <div className="cce-depth-group">
              <TbSlider
                label={`Bevel depth: ${objProps.fxBevelDepth ?? 0}`}
                value={objProps.fxBevelDepth ?? 0}
                min={0}
                max={250}
                step={1}
                onChange={(v) => apply(() => editor?.patchTextFx('bevel', { depth: v }), 'defer')}
                onCommit={commitNow}
              />
              <TbSlider
                label={`Bevel width: ${objProps.fxBevelWidth ?? 34}`}
                value={objProps.fxBevelWidth ?? 34}
                min={0}
                max={250}
                step={1}
                onChange={(v) => apply(() => editor?.patchTextFx('bevel', { size: v }), 'defer')}
                onCommit={commitNow}
              />
            </div>
          )}
          {/* 3D — matrix-gated for shapes/icons until U3 lands the silhouette
              source (DYNAMIC: the checkbox un-disables itself when the cell
              flips). The wiring is already correct for that day. */}
          <label
            className="cce-depth-toggle cce-depth-toggle-3d"
            title={threeDDenied ? extrudeCap.reason : undefined}
          >
            <input
              type="checkbox"
              checked={extrudeOn}
              disabled={threeDDenied}
              onChange={(e) => {
                if (threeDDenied) return;
                apply(
                  () => editor?.patchTextFx('extrude', { depth: e.target.checked ? FX_EXTRUDE_DEFAULTS.depth : 0 }),
                  'now',
                );
              }}
            />
            <DepthTileFace kind="extrude" />
            <span>{threeDDenied ? '3D — soon' : '3D'}</span>
          </label>
        </div>
      </Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="cce-inspector-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

function ShadowControls({ objProps, apply, commitNow, editor }) {
  const { t } = useTranslation('editor');
  const shadow = objProps.shadow;
  const setShadow = (patch, mode) =>
    apply(
      () =>
        editor?.changeShadow({
          color: shadow?.color ?? 'rgba(0,0,0,0.5)',
          blur: shadow?.blur ?? 8,
          offsetX: shadow?.offsetX ?? 4,
          offsetY: shadow?.offsetY ?? 4,
          ...patch,
        }),
      mode,
    );

  return (
    <Section title={t('effects.shadow')}>
      <div className="cce-inspector-row">
        <span>{t('effects.color')}</span>
        <TbColorField
          label={t('effects.shadowColor')}
          value={shadow?.color?.startsWith?.('#') ? shadow.color : '#000000'}
          onChange={(v) => setShadow({ color: v }, 'soon')}
        />
        {shadow && (
          <button
            type="button"
            className="cce-inspector-mini-btn"
            onClick={() => apply(() => editor?.changeShadow(null))}
          >
            {t('effects.remove')}
          </button>
        )}
      </div>
      <TbSlider
        label={t('effects.blurValue', { value: Math.round((shadow?.blur ?? 0) * 10) / 10 })}
        value={shadow?.blur ?? 0}
        min={0}
        max={60}
        step={1}
        onChange={(v) => setShadow({ blur: v }, 'defer')}
        onCommit={commitNow}
      />
      <TbSlider
        label={t('effects.offsetXValue', { value: Math.round((shadow?.offsetX ?? 0) * 10) / 10 })}
        value={shadow?.offsetX ?? 0}
        min={-50}
        max={50}
        step={1}
        onChange={(v) => setShadow({ offsetX: v }, 'defer')}
        onCommit={commitNow}
      />
      <TbSlider
        label={t('effects.offsetYValue', { value: Math.round((shadow?.offsetY ?? 0) * 10) / 10 })}
        value={shadow?.offsetY ?? 0}
        min={-50}
        max={50}
        step={1}
        onChange={(v) => setShadow({ offsetY: v }, 'defer')}
        onCommit={commitNow}
      />
    </Section>
  );
}

function GradientControls({ objProps, apply, editor, title }) {
  const { t } = useTranslation('editor');
  const [color1, setColor1] = useState('#3b82f6');
  const [color2, setColor2] = useState('#8b5cf6');
  const [direction, setDirection] = useState('horizontal');

  return (
    <Section title={title || t('effects.gradientFill')}>
      <div className="cce-inspector-row">
        <input type="color" value={color1} aria-label={t('effects.gradientStart')} onChange={(e) => setColor1(e.target.value)} />
        <span>→</span>
        <input type="color" value={color2} aria-label={t('effects.gradientEnd')} onChange={(e) => setColor2(e.target.value)} />
        <select value={direction} aria-label={t('effects.gradientDirection')} onChange={(e) => setDirection(e.target.value)}>
          <option value="horizontal">{t('effects.horizontal')}</option>
          <option value="vertical">{t('effects.vertical')}</option>
          <option value="diagonal">{t('effects.diagonal')}</option>
        </select>
      </div>
      <div className="cce-inspector-row">
        <button
          type="button"
          className="cce-inspector-mini-btn primary"
          onClick={() => apply(() => editor?.applyGradient(color1, color2, direction))}
        >
          {t('effects.apply')}
        </button>
        {objProps.hasGradientFill && (
          <button
            type="button"
            className="cce-inspector-mini-btn"
            onClick={() => apply(() => editor?.removeGradient())}
          >
            {t('effects.remove')}
          </button>
        )}
      </div>
    </Section>
  );
}

export default function EffectsTab({ editor, selectedObjects }) {
  const { t } = useTranslation('editor');
  const { objProps, apply, commitNow } = useSelectionProps(editor, selectedObjects);
  const obj = selectedObjects[0];
  const type = obj?.type;
  const isText = isTextType(type);
  const isImage = type === 'image';
  const isIcon = obj?.isIcon === true;
  const isPhoto = isImage && !isIcon;
  const isShape = obj && !isText && !isImage && selectedObjects.length === 1 && type !== 'group';

  return (
    <div className="cce-inspector-content">
      {isPhoto && (
        <>
          <Section title={t('effects.adjust')}>
            <TbSlider
              label={t('effects.brightnessValue', { value: (objProps.filters?.brightness ?? 0).toFixed(2) })}
              value={objProps.filters?.brightness ?? 0}
              min={-1}
              max={1}
              step={0.05}
              onChange={(v) => apply(() => editor?.applyImageFilter('Brightness', v), 'defer')}
              onCommit={commitNow}
            />
            <TbSlider
              label={t('effects.contrastValue', { value: (objProps.filters?.contrast ?? 0).toFixed(2) })}
              value={objProps.filters?.contrast ?? 0}
              min={-1}
              max={1}
              step={0.05}
              onChange={(v) => apply(() => editor?.applyImageFilter('Contrast', v), 'defer')}
              onCommit={commitNow}
            />
            <TbSlider
              label={t('effects.saturationValue', { value: (objProps.filters?.saturation ?? 0).toFixed(2) })}
              value={objProps.filters?.saturation ?? 0}
              min={-1}
              max={1}
              step={0.05}
              onChange={(v) => apply(() => editor?.applyImageFilter('Saturation', v), 'defer')}
              onCommit={commitNow}
            />
            <TbSlider
              label={t('effects.blurValue', { value: (objProps.filters?.blur ?? 0).toFixed(2) })}
              value={objProps.filters?.blur ?? 0}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => apply(() => editor?.applyImageFilter('Blur', v), 'defer')}
              onCommit={commitNow}
            />
            <div className="cce-inspector-row">
              <button
                type="button"
                className={`cce-inspector-mini-btn${objProps.filters?.grayscale ? ' on' : ''}`}
                onClick={() => apply(() => editor?.applyImageFilter('Grayscale', !objProps.filters?.grayscale))}
              >
                {t('effects.bw')}
              </button>
              <button
                type="button"
                className={`cce-inspector-mini-btn${objProps.filters?.sepia ? ' on' : ''}`}
                onClick={() => apply(() => editor?.applyImageFilter('Sepia', !objProps.filters?.sepia))}
              >
                {t('effects.sepia')}
              </button>
              <button
                type="button"
                className={`cce-inspector-mini-btn${objProps.filters?.invert ? ' on' : ''}`}
                onClick={() => apply(() => editor?.applyImageFilter('Invert', !objProps.filters?.invert))}
              >
                {t('effects.invert')}
              </button>
              <button
                type="button"
                className="cce-inspector-mini-btn"
                onClick={() => apply(() => editor?.removeAllImageFilters())}
              >
                {t('effects.reset')}
              </button>
            </div>
          </Section>
          <ShadowControls objProps={objProps} apply={apply} commitNow={commitNow} editor={editor} />
          <GradientControls objProps={objProps} apply={apply} editor={editor} title={t('effects.gradientOverlay')} />
        </>
      )}

      {(isShape || isIcon) && (
        <>
          {/* Universal effects (U2): the ribbon's Row-2 EFFECTS/DEPTH unfolded.
              The manual Shadow/Border/Gradient sections below stay as the fine
              tuners (the Shadow tile toggles the slot; this section tunes the
              pixels — the split-button toggle/params grammar, drawer-side). */}
          <ObjectEffectsSection
            objProps={objProps}
            apply={apply}
            commitNow={commitNow}
            editor={editor}
            kind={isIcon ? 'icon' : 'shape'}
          />
          <ShadowControls objProps={objProps} apply={apply} commitNow={commitNow} editor={editor} />
          {isShape && (
            <>
              <Section title={t('effects.borderStyle')}>
                <div className="cce-inspector-row">
                  <select
                    value={objProps.strokeStyle ?? 'solid'}
                    aria-label={t('effects.borderStyle')}
                    onChange={(e) => {
                      const v = e.target.value;
                      apply(() => editor?.changeStrokeDashArray(v === 'solid' ? [] : v === 'dashed' ? [5, 5] : [2, 2]));
                    }}
                  >
                    <option value="solid">{t('effects.solid')}</option>
                    <option value="dashed">{t('effects.dashed')}</option>
                    <option value="dotted">{t('effects.dotted')}</option>
                  </select>
                </div>
              </Section>
              {type === 'rect' && (
                <Section title={t('effects.cornerRadius')}>
                  <div className="cce-inspector-row">
                    <TbNumberField
                      label={t('effects.cornerRadius')}
                      value={objProps.cornerRadius ?? 0}
                      min={0}
                      max={500}
                      onChange={(v) => apply(() => editor?.changeCornerRadius(v), 'soon')}
                    />
                  </div>
                </Section>
              )}
              <GradientControls objProps={objProps} apply={apply} editor={editor} />
            </>
          )}
        </>
      )}

      {selectedObjects.length > 1 && (
        <div className="cce-inspector-empty">
          {t('effects.multiHint')}
        </div>
      )}
    </div>
  );
}
